import economyService from '../services/economy';
import { notify } from './notificationReducer';
import getErrorMsg from '../utils/getErrorMsg';
import type { AppDispatch } from '../store';
import type { EconomyState, LedgerRow, ReduxAction } from '../types/common';

const initialState: EconomyState = {
  balances: null,
  cap: null,
  config: null,
  ledger: { items: [], cursor: null, loading: false, exhausted: false, currency: 'all' },
  loading: false,
  error: null,
};

const economyReducer = (
  state: EconomyState = initialState,
  action: ReduxAction<unknown>
): EconomyState => {
  switch (action.type) {
    case 'ECONOMY_SET_CONFIG':
      return { ...state, config: action.payload as EconomyState['config'] };
    case 'ECONOMY_WALLET_START':
      return { ...state, loading: true, error: null };
    case 'ECONOMY_WALLET_FAIL':
      return { ...state, loading: false, error: String(action.payload ?? 'Unknown error') };
    case 'ECONOMY_SET_BALANCES': {
      const payload = action.payload as { points?: unknown; coins?: unknown; dailyCap?: unknown };
      return {
        ...state,
        loading: false,
        error: null,
        balances: {
          points: (payload.points ?? state.balances?.points ?? null) as any,
          coins: (payload.coins ?? state.balances?.coins ?? null) as any,
        },
        cap: (payload.dailyCap ?? state.cap) as EconomyState['cap'],
      };
    }
    // Reaction and award responses carry authoritative balances, so the wallet
    // stays fresh without a second round trip.
    case 'ECONOMY_PATCH_BALANCES': {
      const patch = action.payload as { coins?: number; points?: number };
      if (!state.balances) return state;
      return {
        ...state,
        balances: {
          points:
            patch.points === undefined
              ? state.balances.points
              : { ...state.balances.points, available: patch.points },
          coins:
            patch.coins === undefined
              ? state.balances.coins
              : { ...state.balances.coins, available: patch.coins },
        },
      };
    }
    case 'ECONOMY_LEDGER_START':
      return { ...state, ledger: { ...state.ledger, loading: true } };
    case 'ECONOMY_LEDGER_RESET':
      return {
        ...state,
        ledger: {
          items: [],
          cursor: null,
          loading: true,
          exhausted: false,
          currency: action.payload as string,
        },
      };
    case 'ECONOMY_LEDGER_PAGE': {
      const page = action.payload as { results: LedgerRow[]; next: string | null; reset?: boolean };
      const items = page.reset ? page.results : [...state.ledger.items, ...page.results];
      return {
        ...state,
        ledger: {
          ...state.ledger,
          items,
          cursor: page.next,
          loading: false,
          exhausted: !page.next,
        },
      };
    }
    case 'ECONOMY_LEDGER_FAIL':
      return { ...state, ledger: { ...state.ledger, loading: false } };
    default:
      return state;
  }
};

export const fetchEconomyConfig = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const config = await economyService.getConfig();
      dispatch({ type: 'ECONOMY_SET_CONFIG', payload: config });
    } catch {
      // Prices fall back to whatever the UI last knew; not worth a toast.
    }
  };
};

export const fetchWallet = () => {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: 'ECONOMY_WALLET_START' });
    try {
      const wallet = await economyService.getWallet();
      dispatch({ type: 'ECONOMY_SET_BALANCES', payload: wallet });
    } catch (err) {
      dispatch({ type: 'ECONOMY_WALLET_FAIL', payload: getErrorMsg(err) });
    }
  };
};

export const loadLedger = (currency: string, cursor?: string | null) => {
  return async (dispatch: AppDispatch) => {
    const reset = !cursor;
    dispatch(
      reset
        ? { type: 'ECONOMY_LEDGER_RESET', payload: currency }
        : { type: 'ECONOMY_LEDGER_START' }
    );
    try {
      const page = await economyService.getLedger(currency, cursor);
      dispatch({
        type: 'ECONOMY_LEDGER_PAGE',
        payload: { results: page.results, next: page.next, reset },
      });
    } catch (err) {
      dispatch({ type: 'ECONOMY_LEDGER_FAIL' });
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };
};

export const giveAward = (
  postId: string,
  awardKey: string,
  awardName: string,
  anonymous: boolean
) => {
  return async (dispatch: AppDispatch) => {
    // One key per attempt: a retry of THIS attempt is free, a deliberate second
    // award is a new key and charges again, which is what a user expects.
    const idempotencyKey = `${postId}:${awardKey}:${Date.now()}:${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const result = await economyService.giveAward(postId, awardKey, anonymous, idempotencyKey);

    dispatch({ type: 'ECONOMY_PATCH_BALANCES', payload: { coins: result.coins } });
    dispatch({ type: 'AWARD_GIVEN', payload: { postId, awardKey } });
    dispatch(notify(`${awardName} sent 🎉`, 'success'));
    return result;
  };
};

export const convertPointsToCoins = (coins: number) => {
  return async (dispatch: AppDispatch) => {
    const result = await economyService.convertPointsToCoins(coins);
    dispatch({
      type: 'ECONOMY_PATCH_BALANCES',
      payload: { coins: result.coins, points: result.points },
    });
    dispatch(notify(`Converted ${result.converted.points} Points into ${result.converted.coins} Coins`, 'success'));
    return result;
  };
};

export default economyReducer;
